/**
 * keyboardInset — sets a global `--kb-inset` CSS variable to the height (px)
 * of the screen area obscured by the on-screen keyboard, derived from
 * `window.visualViewport`. Fixed overlays / bottom sheets read
 * `var(--kb-inset, 0px)` to lift above the keyboard.
 *
 * Why this exists alongside the native @capacitor/keyboard plugin:
 *  - Native app (resize: 'native'): the WebView itself shrinks when the
 *    keyboard opens, so visualViewport.height and window.innerHeight move
 *    together and the computed inset stays ~0 — no double compensation.
 *  - Web / PWA / browsers without `interactive-widget` support: the layout
 *    viewport does NOT shrink, so visualViewport reports the obscured area and
 *    this var carries the keyboard height to the sheets.
 *
 * Idempotent; safe to call once at startup. No-op where visualViewport is
 * unavailable (the layouts then fall back to `0px`).
 */
let started = false;

export function initKeyboardInset(): void {
  if (started || typeof window === 'undefined') return;
  const vv = window.visualViewport;
  if (!vv) return;
  started = true;

  const root = document.documentElement;

  const update = () => {
    // Bottom slice of the layout viewport not covered by the visual viewport.
    const raw = window.innerHeight - vv.height - vv.offsetTop;
    // Ignore small deltas (URL bar show/hide, rounding) — only a real keyboard
    // obscures a meaningful chunk. Avoids layout jitter.
    const inset = raw > 24 ? Math.round(raw) : 0;
    root.style.setProperty('--kb-inset', `${inset}px`);
  };

  vv.addEventListener('resize', update);
  vv.addEventListener('scroll', update);
  update();
}
